import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  ExpandableSection,
  Header,
  Input,
  Modal,
  Multiselect,
  Pagination,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Tabs,
  TextFilter,
  Textarea,
  Toggle,
  TokenGroup,
} from '@cloudscape-design/components';
import { PageLayout, EmptyState, PageSkeleton, PlatformTable, useAuth, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';
import { PlusIcon, SearchIcon } from '../assets/icons/ddsIcons';
import { PersonAutosuggest } from '../rbac/PersonAutosuggest';
import { useActiveAllianceOptions } from '../rbac/useActiveAlliances';

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

interface ResourceAction { action: string[]; applicationresource_id?: string[] }
interface Policy { id: string; name: string; effect: 'allow' | 'deny'; scope: 'global' | 'application-specific'; application_id: string[]; resource_action: ResourceAction[] }
type RoleClassification = 'system' | 'airtable' | 'team' | 'application';
interface Role {
  id: string;
  role_name: string;
  role_key: string;
  scope: 'global' | 'application-specific';
  application_id: string[];
  classification?: RoleClassification;
  requestable?: boolean;
}

interface Assignment {
  userId: string; roleId: string; assignedBy?: string; assignedAt?: string;
  userEmail?: string | null; userName?: string | null;
}
interface UserAccess { user_id: string; platformRoles: string[]; apps: Record<string, { roles: string[]; permissions: string[] }> }

/** Entry shape served by GET /api/registry/all and PUT /api/registry/:appId
 *  (see AppRegistryAdmin.tsx) — kept minimal here to just what RolesTab needs
 *  to safely read-then-write an app's `requiredRole` without touching its
 *  other fields. */
interface RegistryAppEntry {
  appId: string;
  displayName: string;
  remoteUrl?: string;
  externalUrl?: string;
  navIcon?: string;
  navOrder?: number;
  requiredRole?: string[];
  requiredAlliance?: string[];
  enabled: boolean;
  aiEnabled?: boolean;
  airtableEnabled?: boolean;
  airtableBaseId?: string;
  description?: string;
}

const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
/** A team name becomes both the role's internal id and its role_key (same
 *  convention every built-in platform role already uses — platform-roles.ts's
 *  `orgWideRole` sets `role_key: id`), so admins creating an ad-hoc team role
 *  never have to think about id/key at all. */
const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const EFFECT_OPTIONS = [
  { value: 'allow', label: 'Allow' },
  { value: 'deny', label: 'Deny' },
];

/**
 * RBAC admin — manage the real Policy Decision Point's policies, roles, and
 * user→role assignments. Gated at the router level by RequirePermission(Rbac:Manage);
 * the API independently enforces the same gate on every write. See
 * docs/adr/0003-rbac-approach.md and the gp-platform skill references/rbac.md.
 *
 * Every tab's tables use the SDK's PlatformTable (standard header/counter,
 * a persistent retryable error state instead of a load-failure toast). An
 * opt-in Microsoft directory (Graph) search is available on the person
 * picker in Manage Access only (ADR 0019) — App Access Lookup
 * stays roster-only on purpose, since a person found via tenant search but
 * never logged in has no existing access to look up.
 */
export default function RbacAdmin() {
  const { accessToken } = useAuth();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  return (
    <PageLayout title="Role Based Access Control" description="Manage RBAC policies, roles, and user assignments.">
      <Tabs
        tabs={[
          { id: 'roster', label: 'Roster', content: <RosterTab api={api} /> },
          { id: 'lookup-access', label: 'App Access Lookup', content: <LookupAccessTab api={api} /> },
          { id: 'grant-access', label: 'Manage Access', content: <GrantAccessTab api={api} /> },
          { id: 'roles', label: 'Roles', content: <RolesTab api={api} /> },
          { id: 'audit-log', label: 'Audit Log', content: <AuditTab api={api} /> },
          // Policies tab hidden (not removed): every real permission bundle in
          // use today is pre-seeded in code (platform-roles.ts's admin bundle,
          // each app's own manifests.ts) rather than hand-created here, and
          // there's no UI anywhere to attach a created policy to a role
          // (POST /rbac/rolepolicy) -- so this page lets you create an object
          // with no way to make it do anything. Re-add to this array if that
          // changes (a real attach-to-role UI gets built).
        ]}
      />
    </PageLayout>
  );
}

type Api = ReturnType<typeof createApiClient>;

// ── Roster ────────────────────────────────────────────────────────────────────
interface RosterRoleEntry { roleId: string; status: 'active' | 'pending'; assignedAt?: string }
interface RosterAllianceEntry { allianceName: string; status: 'active' | 'pending'; sources: string[]; isPrimary?: boolean }
interface RosterCohortEntry { cohortId: string; cohortName: string }
interface RosterPerson {
  email: string;
  name: string | null;
  businessUnit: string | null;
  roles: RosterRoleEntry[];
  alliances: RosterAllianceEntry[];
  cohorts: RosterCohortEntry[];
}

type RosterView = 'person' | 'role' | 'alliance';
const ROSTER_VIEW_OPTIONS: { value: RosterView; label: string }[] = [
  { value: 'person', label: 'By person' },
  { value: 'role', label: 'By role' },
  { value: 'alliance', label: 'By alliance' },
];

interface PersonRef { name: string | null; email: string; status: 'active' | 'pending' }
interface GroupRow { key: string; label: string; people: PersonRef[] }
/** A generic labeled thing with bound/pending status — a role, an alliance,
 *  or a person, all rendered the same small tagged-list way. */
interface StatusTag { key: string; label: string; status: 'active' | 'pending' }

const personDisplayName = (p: { name: string | null; email: string }) => p.name || p.email;

function StatusTagList({ tags }: { tags: StatusTag[] }) {
  if (tags.length === 0) return <>—</>;
  return (
    <SpaceBetween size="xxs">
      {tags.map((t) => (
        <StatusIndicator key={`${t.key}-${t.status}`} type={t.status === 'active' ? 'success' : 'pending'}>
          {t.label}
        </StatusIndicator>
      ))}
    </SpaceBetween>
  );
}

/**
 * One collapsible section per group (role or alliance), each listing its
 * people — collapsed by default so a large group (e.g. an alliance with 30+
 * people) doesn't dominate the page; "Expand all"/"Collapse all" above the
 * list for browsing everything at once. Expanded state is keyed by the
 * group's own key and controlled by the caller, so switching between the
 * "by role" and "by alliance" views doesn't share or clobber each other's
 * expand state.
 */
function GroupedRosterList({
  rows,
  expandedKeys,
  onToggle,
  emptyDescription,
}: {
  rows: GroupRow[];
  expandedKeys: Set<string>;
  onToggle: (key: string, expanded: boolean) => void;
  emptyDescription: string;
}) {
  if (rows.length === 0) return <EmptyState title="No matches" description={emptyDescription} />;
  return (
    <SpaceBetween size="xs">
      {rows.map((r) => (
        <ExpandableSection
          key={r.key}
          variant="container"
          headerText={r.label}
          headerCounter={`(${r.people.length})`}
          expanded={expandedKeys.has(r.key)}
          onChange={(e) => onToggle(r.key, e.detail.expanded)}
        >
          <StatusTagList tags={r.people.map((p) => ({ key: p.email, label: personDisplayName(p), status: p.status }))} />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
}

/**
 * One row per person by default, merging real (bound) role assignments and
 * alliance grants with AirTable-sync pending-bind entries still waiting on
 * that person's first login. Deliberately one unified view, not two — from
 * an admin's perspective "who has, or will have, what" is one question; the
 * pending/bound distinction only matters at the enforcement layer (see
 * pendingBinds.ts), not for browsing who's who.
 *
 * Also pivots to "by role" / "by alliance" — one row per role/alliance
 * listing its people — since "who's in this role" is just as common a
 * question as "what does this person have," and isn't answerable by sorting
 * a per-person table alone (one person can hold several roles/alliances).
 */
function RosterTab({ api }: { api: Api }) {
  const [people, setPeople] = useState<RosterPerson[]>([]);
  const [roleNameById, setRoleNameById] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [view, setView] = useState<RosterView>('person');
  const [sortValue, setSortValue] = useState('name-asc');
  const [expandedRoleKeys, setExpandedRoleKeys] = useState<Set<string>>(new Set());
  const [expandedAllianceKeys, setExpandedAllianceKeys] = useState<Set<string>>(new Set());
  const [personPageIndex, setPersonPageIndex] = useState(1);
  const [rolePageIndex, setRolePageIndex] = useState(1);
  const [alliancePageIndex, setAlliancePageIndex] = useState(1);

  const load = async () => {
    try {
      const [roster, roles] = await Promise.all([
        api.get<RosterPerson[]>('/api/rbac/roster'),
        api.get<Role[]>('/api/rbac/role'),
      ]);
      setPeople(roster);
      setRoleNameById(new Map(roles.map((r) => [r.id, r.role_name])));
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);
  const retry = () => {
    setLoading(true);
    load();
  };

  // A person-view sort value ("name-asc") means nothing in a grouped view
  // (and vice versa for "count-desc") — reset to that view's default rather
  // than carry over a stale, meaningless selection.
  useEffect(() => {
    setSortValue(view === 'person' ? 'name-asc' : 'label-asc');
  }, [view]);
  // The result set underneath has shifted whenever the view, sort, or search
  // changes — stay on page 1 rather than showing a now-meaningless page N.
  useEffect(() => {
    setPersonPageIndex(1);
    setRolePageIndex(1);
    setAlliancePageIndex(1);
  }, [filterText, sortValue, view]);

  const roleLabel = (roleId: string) => roleNameById.get(roleId) ?? roleId;

  if (loading) return <PageSkeleton label="Loading roster…" />;

  const needle = filterText.trim().toLowerCase();
  const personMatches = (p: PersonRef) => p.email.toLowerCase().includes(needle) || (p.name?.toLowerCase().includes(needle) ?? false);

  // ── Person view ──────────────────────────────────────────────────────────
  let personRows = !needle
    ? people
    : people.filter(
        (p) =>
          p.email.toLowerCase().includes(needle) ||
          (p.name?.toLowerCase().includes(needle) ?? false) ||
          (p.businessUnit?.toLowerCase().includes(needle) ?? false) ||
          p.roles.some((r) => roleLabel(r.roleId).toLowerCase().includes(needle)) ||
          p.alliances.some((a) => a.allianceName.toLowerCase().includes(needle)) ||
          p.cohorts.some((c) => c.cohortName.toLowerCase().includes(needle)),
      );
  personRows = [...personRows].sort((a, b) => {
    switch (sortValue) {
      case 'name-desc': return personDisplayName(b).localeCompare(personDisplayName(a));
      case 'email-asc': return a.email.localeCompare(b.email);
      case 'email-desc': return b.email.localeCompare(a.email);
      default: return personDisplayName(a).localeCompare(personDisplayName(b)); // name-asc
    }
  });

  // ── Role / alliance pivots ───────────────────────────────────────────────
  const buildGroups = (get: (p: RosterPerson) => { key: string; label: string; status: 'active' | 'pending' }[]) => {
    const groups = new Map<string, GroupRow>();
    for (const p of people) {
      for (const g of get(p)) {
        const row = groups.get(g.key) ?? { key: g.key, label: g.label, people: [] };
        row.people.push({ name: p.name, email: p.email, status: g.status });
        groups.set(g.key, row);
      }
    }
    for (const row of groups.values()) row.people.sort((a, b) => personDisplayName(a).localeCompare(personDisplayName(b)));
    return [...groups.values()];
  };
  const sortGroups = (rows: GroupRow[]) =>
    [...rows].sort((a, b) => {
      switch (sortValue) {
        case 'label-desc': return b.label.localeCompare(a.label);
        case 'count-desc': return b.people.length - a.people.length;
        case 'count-asc': return a.people.length - b.people.length;
        default: return a.label.localeCompare(b.label); // label-asc
      }
    });
  const filterGroups = (rows: GroupRow[]) =>
    !needle ? rows : rows.filter((r) => r.label.toLowerCase().includes(needle) || r.people.some(personMatches));

  const roleGroupsAll = buildGroups((p) => p.roles.map((r) => ({ key: r.roleId, label: roleLabel(r.roleId), status: r.status })));
  const allianceGroupsAll = buildGroups((p) => p.alliances.map((a) => ({ key: a.allianceName, label: a.allianceName, status: a.status })));
  const roleRows = sortGroups(filterGroups(roleGroupsAll));
  const allianceRows = sortGroups(filterGroups(allianceGroupsAll));

  const toggleKey = (setFn: typeof setExpandedRoleKeys) => (key: string, expanded: boolean) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(key);
      else next.delete(key);
      return next;
    });
  const toggleRoleKey = toggleKey(setExpandedRoleKeys);
  const toggleAllianceKey = toggleKey(setExpandedAllianceKeys);
  const expandAllRoles = () => setExpandedRoleKeys(new Set(roleRows.map((r) => r.key)));
  const collapseAllRoles = () => setExpandedRoleKeys(new Set());
  const expandAllAlliances = () => setExpandedAllianceKeys(new Set(allianceRows.map((r) => r.key)));
  const collapseAllAlliances = () => setExpandedAllianceKeys(new Set());

  const SORT_OPTIONS_BY_VIEW: Record<RosterView, { value: string; label: string }[]> = {
    person: [
      { value: 'name-asc', label: 'Name (A→Z)' },
      { value: 'name-desc', label: 'Name (Z→A)' },
      { value: 'email-asc', label: 'Email (A→Z)' },
      { value: 'email-desc', label: 'Email (Z→A)' },
    ],
    role: [
      { value: 'label-asc', label: 'Role (A→Z)' },
      { value: 'label-desc', label: 'Role (Z→A)' },
      { value: 'count-desc', label: 'Most people first' },
      { value: 'count-asc', label: 'Fewest people first' },
    ],
    alliance: [
      { value: 'label-asc', label: 'Alliance (A→Z)' },
      { value: 'label-desc', label: 'Alliance (Z→A)' },
      { value: 'count-desc', label: 'Most people first' },
      { value: 'count-asc', label: 'Fewest people first' },
    ],
  };
  const sortOptions = SORT_OPTIONS_BY_VIEW[view];
  const totalForView = view === 'person' ? people.length : view === 'role' ? roleGroupsAll.length : allianceGroupsAll.length;
  const shownCount = view === 'person' ? personRows.length : view === 'role' ? roleRows.length : allianceRows.length;

  const PERSON_PAGE_SIZE = 25;
  const personPagesCount = Math.max(1, Math.ceil(personRows.length / PERSON_PAGE_SIZE));
  const personPageIndexSafe = Math.min(personPageIndex, personPagesCount);
  const pagedPersonRows = personRows.slice((personPageIndexSafe - 1) * PERSON_PAGE_SIZE, personPageIndexSafe * PERSON_PAGE_SIZE);

  const GROUP_PAGE_SIZE = 10;
  const rolePagesCount = Math.max(1, Math.ceil(roleRows.length / GROUP_PAGE_SIZE));
  const rolePageIndexSafe = Math.min(rolePageIndex, rolePagesCount);
  const pagedRoleRows = roleRows.slice((rolePageIndexSafe - 1) * GROUP_PAGE_SIZE, rolePageIndexSafe * GROUP_PAGE_SIZE);
  const alliancePagesCount = Math.max(1, Math.ceil(allianceRows.length / GROUP_PAGE_SIZE));
  const alliancePageIndexSafe = Math.min(alliancePageIndex, alliancePagesCount);
  const pagedAllianceRows = allianceRows.slice((alliancePageIndexSafe - 1) * GROUP_PAGE_SIZE, alliancePageIndexSafe * GROUP_PAGE_SIZE);

  return (
    <SpaceBetween size="m">
      {loadError ? (
        <Alert type="error" header="Failed to load roster" action={<Button onClick={retry} iconName="refresh">Retry</Button>}>
          {loadError}
        </Alert>
      ) : people.length === 0 ? (
        <EmptyState
          title="Nobody yet"
          description="Real role/alliance assignments and pending AirTable-sync binds will show up here."
        />
      ) : (
        <>
          <SpaceBetween direction="horizontal" size="s">
            <FormField label="View">
              <Select
                selectedOption={ROSTER_VIEW_OPTIONS.find((o) => o.value === view) ?? null}
                options={ROSTER_VIEW_OPTIONS}
                onChange={(e) => setView(e.detail.selectedOption.value as RosterView)}
              />
            </FormField>
            <FormField label="Sort">
              <Select
                selectedOption={sortOptions.find((o) => o.value === sortValue) ?? null}
                options={sortOptions}
                onChange={(e) => setSortValue(e.detail.selectedOption.value as string)}
              />
            </FormField>
          </SpaceBetween>

          {view === 'person' ? (
            <PlatformTable
              title="Roster — everyone's role and alliance scope, bound or pending"
              items={pagedPersonRows}
              totalCount={personRows.length}
              variant="container"
              filter={
                <TextFilter
                  filteringText={filterText}
                  onChange={(e) => setFilterText(e.detail.filteringText)}
                  filteringPlaceholder="Search by name, email, role, or alliance"
                  countText={`${shownCount} of ${people.length}`}
                />
              }
              pagination={
                <Pagination
                  currentPageIndex={personPageIndexSafe}
                  pagesCount={personPagesCount}
                  onChange={(e) => setPersonPageIndex(e.detail.currentPageIndex)}
                />
              }
              columnDefinitions={[
                { header: 'Name', cell: (p) => p.name || '—' },
                { header: 'Email', cell: (p) => p.email },
                { header: 'Business Unit', cell: (p) => p.businessUnit || '—' },
                { header: 'Roles', cell: (p) => <StatusTagList tags={p.roles.map((r) => ({ key: r.roleId, label: roleLabel(r.roleId), status: r.status }))} /> },
                {
                  header: 'Alliances',
                  cell: (p) => (
                    <StatusTagList
                      tags={[
                        // Cohort membership shown as one tag per cohort, not
                        // every member alliance individually — the grant
                        // itself still covers all of them, this is just
                        // display; cohort membership is never duplicated
                        // into an individual GROUP# alliance pick, so there's
                        // no overlap with the direct alliances listed below.
                        ...p.cohorts.map((c) => ({ key: `cohort-${c.cohortId}`, label: `Cohort: ${c.cohortName}`, status: 'active' as const })),
                        ...p.alliances.map((a) => ({ key: a.allianceName, label: a.isPrimary ? `${a.allianceName} ★ Primary` : a.allianceName, status: a.status })),
                      ]}
                    />
                  ),
                },
              ]}
              empty={<EmptyState title="No matches" description="Nobody matches that search." />}
            />
          ) : view === 'role' ? (
            <SpaceBetween size="s">
              <TextFilter
                filteringText={filterText}
                onChange={(e) => setFilterText(e.detail.filteringText)}
                filteringPlaceholder="Search by role, name, or email"
                countText={`${shownCount} of ${totalForView}`}
              />
              <Header
                counter={`(${shownCount})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={expandAllRoles}>Expand all</Button>
                    <Button onClick={collapseAllRoles}>Collapse all</Button>
                  </SpaceBetween>
                }
              >
                Roster by role
              </Header>
              <GroupedRosterList
                rows={pagedRoleRows}
                expandedKeys={expandedRoleKeys}
                onToggle={toggleRoleKey}
                emptyDescription="No role matches that search."
              />
              <Pagination
                currentPageIndex={rolePageIndexSafe}
                pagesCount={rolePagesCount}
                onChange={(e) => setRolePageIndex(e.detail.currentPageIndex)}
              />
            </SpaceBetween>
          ) : (
            <SpaceBetween size="s">
              <TextFilter
                filteringText={filterText}
                onChange={(e) => setFilterText(e.detail.filteringText)}
                filteringPlaceholder="Search by alliance, name, or email"
                countText={`${shownCount} of ${totalForView}`}
              />
              <Header
                counter={`(${shownCount})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={expandAllAlliances}>Expand all</Button>
                    <Button onClick={collapseAllAlliances}>Collapse all</Button>
                  </SpaceBetween>
                }
              >
                Roster by alliance
              </Header>
              <GroupedRosterList
                rows={pagedAllianceRows}
                expandedKeys={expandedAllianceKeys}
                onToggle={toggleAllianceKey}
                emptyDescription="No alliance matches that search."
              />
              <Pagination
                currentPageIndex={alliancePageIndexSafe}
                pagesCount={alliancePagesCount}
                onChange={(e) => setAlliancePageIndex(e.detail.currentPageIndex)}
              />
            </SpaceBetween>
          )}
        </>
      )}
    </SpaceBetween>
  );
}

// ── Policies ──────────────────────────────────────────────────────────────────
function _PoliciesTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [items, setItems] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Policy | null>(null);
  const [raText, setRaText] = useState('[]');
  const [raError, setRaError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setItems(await api.get<Policy[]>('/api/rbac/policy'));
    } catch (e) {
      notify({ type: 'error', header: 'Could not load policies', content: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);

  const open = (p: Policy | null) => {
    const draft = p ?? { id: '', name: '', effect: 'allow' as const, scope: 'application-specific' as const, application_id: [], resource_action: [] };
    setEditing(draft);
    setRaText(JSON.stringify(draft.resource_action, null, 2));
    setRaError(null);
  };

  const save = async () => {
    if (!editing) return;
    let resource_action: ResourceAction[];
    try {
      resource_action = JSON.parse(raText);
    } catch {
      setRaError('Not valid JSON.');
      return;
    }
    setRaError(null);
    setSaving(true);
    try {
      await api.post('/api/rbac/policy', { ...editing, resource_action });
      notify({ type: 'success', header: 'Policy saved', content: `Saved policy "${editing.id}".` });
      setEditing(null);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof Policy>(k: K, v: Policy[K]) => setEditing((p) => (p ? { ...p, [k]: v } : p));

  if (loading) return <PageSkeleton label="Loading policies…" />;

  return (
    <SpaceBetween size="m">
      {items.length === 0 ? (
        <EmptyState
          title="No policies"
          description="A policy is a reusable rule that allows or denies a set of resource actions (e.g. Widget:Update). Attach a policy to a role — it grants nothing on its own."
          action={<Button iconSvg={<PlusIcon />} onClick={() => open(null)}>Add policy</Button>}
        />
      ) : (
        <Table
          items={items}
          variant="container"
          header={<Header counter={`(${items.length})`} actions={<Button iconSvg={<PlusIcon />} onClick={() => open(null)}>Add policy</Button>}>Policies</Header>}
          columnDefinitions={[
            { header: 'ID', cell: (i) => i.id },
            { header: 'Name', cell: (i) => i.name },
            { header: 'Effect', cell: (i) => i.effect },
            { header: 'Apps', cell: (i) => i.application_id.join(', ') || 'org-wide' },
            { header: 'Edit', cell: (i) => <Button variant="inline-link" onClick={() => open(i)}>Edit</Button> },
          ]}
        />
      )}
      {editing && (
        <Modal
          visible
          header={editing.id ? `Edit ${editing.id}` : 'Add policy'}
          onDismiss={() => setEditing(null)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setEditing(null)}>Cancel</Button>
                <Button variant="primary" loading={saving} onClick={save}>Save</Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField label="ID">
              <Input value={editing.id} disabled={!!items.find((i) => i.id === editing.id)} onChange={(e) => set('id', e.detail.value)} />
            </FormField>
            <FormField label="Name">
              <Input value={editing.name} onChange={(e) => set('name', e.detail.value)} />
            </FormField>
            <FormField label="Effect">
              <Select
                selectedOption={EFFECT_OPTIONS.find((o) => o.value === editing.effect) ?? null}
                options={EFFECT_OPTIONS}
                onChange={(e) => set('effect', e.detail.selectedOption.value as Policy['effect'])}
              />
            </FormField>
            <FormField label="Apps" description="Comma-separated appIds; leave empty for org-wide.">
              <Input value={editing.application_id.join(', ')} onChange={(e) => set('application_id', csv(e.detail.value))} />
            </FormField>
            <FormField
              label="resource_action (JSON)"
              description='e.g. [{"action":["Widget:Update"],"applicationresource_id":["w1"]}]'
              errorText={raError ?? undefined}
            >
              <Textarea value={raText} onChange={(e) => { setRaText(e.detail.value); setRaError(null); }} rows={6} />
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </SpaceBetween>
  );
}

// ── Roles ─────────────────────────────────────────────────────────────────────
function RolesTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [roles, setRoles] = useState<Role[]>([]);
  const [apps, setApps] = useState<RegistryAppEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Role | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [grantAppOptions, setGrantAppOptions] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setRoles(await api.get<Role[]>('/api/rbac/role'));
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Best-effort, separate from the main load: the "grant access to apps"
    // picklist just goes empty if this fails (e.g. Registry:Manage missing
    // in some non-standard permission setup) rather than blocking the tab.
    api.get<RegistryAppEntry[]>('/api/registry/all').then(setApps).catch(() => setApps([]));
  }, [api]);
  const retry = () => {
    setLoading(true);
    load();
  };

  // A team name becomes the role's id/role_key together (slugify) — an admin
  // creating an ad-hoc team never has to think about either. Editing an
  // EXISTING role leaves its id/key alone (isCreating is false); only a
  // brand-new role's name drives them live.
  const set = <K extends keyof Role>(k: K, v: Role[K]) => setEditing((p) => (p ? { ...p, [k]: v } : p));
  const setName = (name: string) => {
    setEditing((p) => (p ? { ...p, role_name: name, ...(isCreating ? { id: slugify(name), role_key: slugify(name) } : {}) } : p));
  };

  const idCollision = isCreating && !!editing?.id && roles.some((r) => r.id === editing.id);

  // Defaults match the common case this form exists for (ADR 0012 decision
  // 6): an admin standing up a new manually-assignable team that should be
  // immediately self-service-joinable, e.g. "Team Rocket" — not a one-off
  // app-scoped or system role, which are the exception here. `classification`
  // and `scope` are never shown on the form below — neither is read by any
  // authorization logic (engine.ts/platform-roles.ts only branch on specific
  // role ids), so there's nothing for an admin to meaningfully decide here.
  const newRole = (): Role => ({
    id: '', role_name: '', role_key: '', scope: 'application-specific', application_id: [],
    classification: 'team', requestable: true,
  });

  const openCreate = () => {
    setIsCreating(true);
    setGrantAppOptions([]);
    setEditing(newRole());
  };
  const openEdit = (role: Role) => {
    setIsCreating(false);
    setGrantAppOptions([]);
    setEditing(role);
  };

  const saveRole = async () => {
    if (!editing || idCollision) return;
    setSaving(true);
    try {
      const saved = await api.post<Role>('/api/rbac/role', editing);

      // Additive-only, best-effort per app: read each selected app's FULL
      // current entry and merge just this role's key into requiredRole,
      // never touching the app's other fields (same read-then-full-write
      // pattern AppRegistryAdmin.tsx's own save() already uses) — a PUT
      // that omitted the rest would silently wipe them (registry.ts's
      // requiredRole has no fallback-to-existing). Deselecting an app here
      // later never removes it from that app's requiredRole; that's App
      // Registry's job.
      const grantResults = await Promise.all(
        grantAppOptions.map(async ({ value: appId }) => {
          try {
            const current = await api.get<RegistryAppEntry>(`/api/registry/${encodeURIComponent(appId)}`);
            const requiredRole = [...new Set([...(current.requiredRole ?? []), saved.role_key])];
            await api.put(`/api/registry/${encodeURIComponent(appId)}`, { ...current, requiredRole });
            return { appId, ok: true as const };
          } catch (e) {
            return { appId, ok: false as const, error: (e as Error).message };
          }
        }),
      );
      const failed = grantResults.filter((r) => !r.ok);
      if (failed.length > 0) {
        notify({
          type: 'error',
          header: 'Role saved, but some app grants failed',
          content: `Saved "${saved.id}". Failed to grant access to: ${failed.map((f) => f.appId).join(', ')}. Retry from App Registry.`,
        });
      } else {
        notify({
          type: 'success',
          header: 'Role saved',
          content: `Saved role "${saved.id}"${grantResults.length > 0 ? ` and granted access to ${grantResults.length} app(s).` : '.'}`,
        });
      }
      setEditing(null);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageSkeleton label="Loading roles…" />;

  return (
    <SpaceBetween size="m">
      {loadError ? (
        <Alert type="error" header="Failed to load roles" action={<Button onClick={retry} iconName="refresh">Retry</Button>}>
          {loadError}
        </Alert>
      ) : roles.length === 0 ? (
        <EmptyState
          title="No roles"
          description="A role is a named team or bundle you assign to users. Its key is the stable identifier returned in /rbac/me that apps check against."
          action={<Button iconSvg={<PlusIcon />} onClick={openCreate}>Add role</Button>}
        />
      ) : (
        <PlatformTable
          title="Roles"
          items={roles}
          variant="container"
          actions={<Button iconSvg={<PlusIcon />} onClick={openCreate}>Add role</Button>}
          columnDefinitions={[
            { header: 'ID', cell: (i) => i.id },
            { header: 'Name', cell: (i) => i.role_name },
            { header: 'Key', cell: (i) => i.role_key },
            { header: 'Apps', cell: (i) => i.application_id.join(', ') || 'org-wide' },
            { header: 'Requestable', cell: (i) => (i.requestable ? 'Yes' : 'No') },
            { header: 'Edit', cell: (i) => <Button variant="inline-link" onClick={() => openEdit(i)}>Edit</Button> },
          ]}
        />
      )}
      {editing && (
        <Modal
          visible
          header={isCreating ? 'Add role' : `Edit ${editing.id}`}
          onDismiss={() => setEditing(null)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setEditing(null)}>Cancel</Button>
                <Button variant="primary" loading={saving} disabled={idCollision} onClick={saveRole}>Save</Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField
              label={isCreating ? 'Team name' : 'Role name'}
              description={isCreating ? `Role key: ${editing.id || '—'}` : undefined}
              errorText={idCollision ? `A role with this name already exists ("${editing.id}"). Choose a different name.` : undefined}
            >
              <Input value={editing.role_name} onChange={(e) => setName(e.detail.value)} placeholder="e.g. Team Rocket" />
            </FormField>
            {!isCreating && (
              <FormField label="Role key" description="Stable identifier returned in /rbac/me (e.g. contracts-admin).">
                <Input value={editing.role_key} onChange={(e) => set('role_key', e.detail.value)} />
              </FormField>
            )}
            <FormField
              label="Grant access to apps"
              description="Adds this role to each selected app's required-role list, so people who get this role can open apps that already existed before it did. Optional — additive only, and only affects apps you select here."
            >
              <Multiselect
                selectedOptions={grantAppOptions}
                placeholder="Choose one or more apps"
                options={apps.map((a) => ({ value: a.appId, label: a.displayName }))}
                onChange={(e) => setGrantAppOptions(e.detail.selectedOptions as { value: string; label: string }[])}
                filteringType="auto"
              />
            </FormField>
            <FormField
              label="Requestable"
              description="Lets any user self-service-request this role (Access Requests page). System/AirTable-sourced roles can never be made requestable, even with this on."
            >
              <Toggle checked={editing.requestable ?? false} onChange={(e) => set('requestable', e.detail.checked)}>
                Requestable by anyone
              </Toggle>
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </SpaceBetween>
  );
}

// ── Role & Alliance Assignments ─────────────────────────────────────────────────
interface AllianceGrant {
  userId: string;
  allianceId: string;
  sources: string[];
  isAllBypass?: boolean;
  userEmail?: string | null;
  userName?: string | null;
}

// A pinned option inside the same Multiselect as real alliance names — the
// universal "All alliances" bypass (SCOPE#ALL) is a distinct grant
// mechanism (alliances.ts's grantAllBypass/revokeAllBypass), not itself a
// named alliance, so the value is split out before the API call rather than
// treated as one more entry in `allianceNames`.
const ALL_BYPASS_VALUE = '__all__';
const ALL_BYPASS_LABEL = 'All alliances (universal bypass)';

/**
 * "App Access Lookup" tab — read-only lookup of an EXISTING person's current
 * COMPUTED, per-app access (roles + resolved permissions), via
 * `GET /rbac/user/:id/access` — the review counterpart to GrantAccessTab's
 * provisioning. Distinct from Roster's search: Roster browses everyone's raw
 * role/alliance grants as stored; this resolves one person's grants down
 * into what they can actually do in each app — see the in-tab Alert below
 * for the same explanation surfaced to admins. Deliberately roster-only (no
 * `api` prop on PersonAutosuggest, so no Graph toggle — ADR 0019): someone
 * found via Microsoft directory search but never logged in has zero real
 * access by definition, so there's nothing here to look up for them. Same
 * reasoning AccessRequests.tsx's ImpersonateTab already applies to its own
 * roster-only picker.
 */
function LookupAccessTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [roster, setRoster] = useState<RosterPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [userId, setUserId] = useState('');
  const [access, setAccess] = useState<UserAccess | null>(null);
  const [lookingUp, setLookingUp] = useState(false);

  const load = async () => {
    try {
      setRoster(await api.get<RosterPerson[]>('/api/rbac/roster'));
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);
  const retry = () => {
    setLoading(true);
    load();
  };

  const lookup = async () => {
    if (!userId) return;
    setLookingUp(true);
    setAccess(null);
    try {
      setAccess(await api.get<UserAccess>(`/api/rbac/user/${encodeURIComponent(userId)}/access`));
    } catch (e) {
      notify({ type: 'error', header: 'Lookup failed', content: (e as Error).message });
    } finally {
      setLookingUp(false);
    }
  };

  const accessRows = access ? Object.entries(access.apps).map(([appId, a]) => ({ appId, ...a })) : [];

  if (loading) return <PageSkeleton label="Loading roster…" />;

  return (
    <SpaceBetween size="l">
      {loadError ? (
        <Alert type="error" header="Failed to load roster" action={<Button onClick={retry} iconName="refresh">Retry</Button>}>
          {loadError}
        </Alert>
      ) : (
        <>
          <Alert type="info" header="How this differs from Roster">
            Roster shows everyone's raw role and alliance grants as stored. This tab resolves ONE
            person's grants into what they can actually do, app by app — use it to answer "why
            can/can't this person do X in app Y," not "what does this person have."
          </Alert>
          <SpaceBetween size="m">
            <FormField label="User" description="Search existing people by name or email — no directory search here, since someone with no existing access has nothing to look up.">
              <PersonAutosuggest value={userId} onChange={setUserId} people={roster} />
            </FormField>
            <Button iconSvg={<SearchIcon />} loading={lookingUp} onClick={lookup}>Look up effective access</Button>
          </SpaceBetween>

          {access && (
            <PlatformTable
              title={`Effective access for ${access.user_id}`}
              items={accessRows}
              variant="container"
              columnDefinitions={[
                { header: 'App', cell: (i) => i.appId },
                { header: 'Roles (key)', cell: (i) => i.roles.join(', ') || '—' },
                { header: 'Allowed actions', cell: (i) => i.permissions.join(', ') || '—' },
              ]}
              empty={<EmptyState title="No effective access" description="This user has no resolved roles or permissions in any app." />}
            />
          )}
        </>
      )}
    </SpaceBetween>
  );
}

/**
 * "Manage Access" tab — direct admin grant/revoke for both roles and
 * alliance scope, batched over one or more people at once — the manual
 * counterpart to the AirTable sync (which only covers LAP/AM titles): a
 * manually-assigned team's own alliance access has nowhere else to be
 * granted from. Batch is a real backend capability
 * (`POST /rbac/role/:id/assignment` and `/rbac/alliance/assignment` both
 * take `assign`/`remove` arrays and resolve every target — see rbac.ts),
 * not a client-side loop; picking several people and clicking Assign once
 * is one request, same as picking one. Alliance grants made here are
 * source-tagged `admin_direct` (alliances.ts), so revoking here never
 * touches a grant the sync or a self-service approval also asserts for the
 * same alliance. The person picker offers an opt-in Microsoft directory
 * search (ADR 0019) — provisioning is exactly the case where finding
 * someone who's never touched the platform actually matters; see
 * LookupAccessTab for the read-only counterpart, which deliberately omits
 * it.
 *
 * The two tables below narrow to just the added people's own grants once
 * anyone is in the People picker — reviewing "what do they already have"
 * is the natural companion to "what am I about to grant/revoke them."
 * With nobody added, they list EVERY existing grant instead, for the
 * distinct bulk-cleanup workflow (e.g. an offboarding cohort you're
 * finding by browsing, not by name) — each row supports both a per-row
 * inline Revoke and multi-row selection + a "Revoke selected" bulk action.
 * Bulk revoke groups selected rows by roleId/allianceId (the endpoints are
 * scoped that way) and fires one request per group, not one per row.
 */
interface PickedPerson {
  email: string;
  name: string | null;
}

function GrantAccessTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [roles, setRoles] = useState<Role[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [allianceGrants, setAllianceGrants] = useState<AllianceGrant[]>([]);
  const [roster, setRoster] = useState<RosterPerson[]>([]);
  const allianceCatalog = useActiveAllianceOptions(api);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // One or more people to assign/revoke for at once (batch — the backend's
  // assign/remove arrays already support this, see the doc comment above).
  // `draftPerson*` is the in-progress autosuggest value before "Add person"
  // moves it into `selectedPeople` as a removable token.
  const [selectedPeople, setSelectedPeople] = useState<PickedPerson[]>([]);
  const [draftPersonValue, setDraftPersonValue] = useState('');
  const [draftPersonName, setDraftPersonName] = useState<string | null>(null);
  const [role, setRole] = useState<{ value: string; label: string } | null>(null);
  const [allianceNames, setAllianceNames] = useState<{ value: string; label: string }[]>([]);
  const [busy, setBusy] = useState(false);
  // Inline validation for the assign/revoke forms below, instead of a toast
  // that vanishes — `peopleError` is shared by both sections since they
  // share one People picker.
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [allianceError, setAllianceError] = useState<string | null>(null);
  const [assignmentPageIndex, setAssignmentPageIndex] = useState(1);
  const [grantPageIndex, setGrantPageIndex] = useState(1);
  const [selectedAssignmentRows, setSelectedAssignmentRows] = useState<Assignment[]>([]);
  const [selectedGrantRows, setSelectedGrantRows] = useState<AllianceGrant[]>([]);

  const addPerson = () => {
    const email = draftPersonValue.trim();
    if (!email) return;
    setSelectedPeople((prev) =>
      prev.some((p) => p.email.toLowerCase() === email.toLowerCase()) ? prev : [...prev, { email, name: draftPersonName }],
    );
    setDraftPersonValue('');
    setDraftPersonName(null);
    setPeopleError(null);
  };
  const removePersonAt = (index: number) => setSelectedPeople((prev) => prev.filter((_, i) => i !== index));

  const load = async () => {
    try {
      const [r, a, g, p] = await Promise.all([
        api.get<Role[]>('/api/rbac/role'),
        api.get<Assignment[]>('/api/rbac/assignment'),
        api.get<AllianceGrant[]>('/api/rbac/alliance-grant'),
        api.get<RosterPerson[]>('/api/rbac/roster'),
      ]);
      setRoles(r);
      setAssignments(a);
      setAllianceGrants(g);
      setRoster(p);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);
  const retry = () => {
    setLoading(true);
    load();
  };
  // The two tables below narrow to just the added people's own grants once
  // anyone is added — "who has what" for exactly the batch you're about to
  // act on, addressing the same question the People picker's selection was
  // implicitly asking. An empty picker still shows everyone (the bulk-
  // cleanup workflow, where you're not targeting anyone in particular).
  // Reset to page 1 whenever the selection changes, since the visible set
  // just changed size.
  useEffect(() => {
    setAssignmentPageIndex(1);
    setGrantPageIndex(1);
  }, [selectedPeople]);

  const roleLabel = (id: string) => roles.find((x) => x.id === id)?.role_name ?? id;

  const peopleLabel = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

  // Batch assign/revoke for the top form — every person currently added to
  // `selectedPeople` at once, one request (see the doc comment above on why
  // this is genuinely one API call, not a client-side loop).
  const actRole = async (mode: 'assign' | 'remove') => {
    const missingPeople = selectedPeople.length === 0;
    const missingRole = !role?.value;
    setPeopleError(missingPeople ? 'Add at least one person.' : null);
    setRoleError(missingRole ? 'Choose a role.' : null);
    if (missingPeople || missingRole || !role) return;
    const targets = selectedPeople.map((p) => p.email);
    const assignNames = Object.fromEntries(selectedPeople.filter((p) => p.name).map((p) => [p.email, p.name as string]));
    setBusy(true);
    try {
      await api.post(`/api/rbac/role/${role.value}/assignment`, {
        [mode]: targets,
        ...(mode === 'assign' && Object.keys(assignNames).length > 0 ? { assignNames } : {}),
      });
      notify({
        type: 'success',
        header: mode === 'assign' ? 'Role assigned' : 'Role revoked',
        content: `${mode === 'assign' ? 'Assigned' : 'Revoked'} ${roleLabel(role.value)} ${mode === 'assign' ? 'to' : 'from'} ${peopleLabel(targets.length)}.`,
      });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  // Single-row inline revoke on the "Existing role assignments" table —
  // bypasses the People picker entirely, since the row already identifies
  // exactly who and what to revoke.
  const revokeRoleRow = async (userId: string, roleId: string) => {
    setBusy(true);
    try {
      await api.post(`/api/rbac/role/${roleId}/assignment`, { remove: [userId] });
      notify({ type: 'success', header: 'Role revoked', content: `Revoked ${roleLabel(roleId)} from ${userId}.` });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  // Multi-row bulk revoke on the same table — groups the current selection
  // by roleId (the endpoint is scoped to one role per call) and fires one
  // request per group, not one per row.
  const bulkRevokeRoles = async () => {
    if (selectedAssignmentRows.length === 0) return;
    setBusy(true);
    try {
      const byRole = new Map<string, string[]>();
      for (const a of selectedAssignmentRows) byRole.set(a.roleId, [...(byRole.get(a.roleId) ?? []), a.userId]);
      await Promise.all(
        [...byRole.entries()].map(([roleId, userIds]) => api.post(`/api/rbac/role/${roleId}/assignment`, { remove: userIds })),
      );
      notify({ type: 'success', header: 'Roles revoked', content: `Revoked ${selectedAssignmentRows.length} role assignment(s).` });
      setSelectedAssignmentRows([]);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Bulk revoke failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const actAlliance = async (mode: 'assign' | 'remove') => {
    const wantsAllBypass = allianceNames.some((a) => a.value === ALL_BYPASS_VALUE);
    const names = allianceNames.filter((a) => a.value !== ALL_BYPASS_VALUE).map((a) => a.value);
    const missingPeople = selectedPeople.length === 0;
    const missingAlliance = names.length === 0 && !wantsAllBypass;
    setPeopleError(missingPeople ? 'Add at least one person.' : null);
    setAllianceError(missingAlliance ? 'Choose at least one alliance (or "All alliances").' : null);
    if (missingPeople || missingAlliance) return;
    const targets = selectedPeople.map((p) => p.email);
    const assignNames = Object.fromEntries(selectedPeople.filter((p) => p.name).map((p) => [p.email, p.name as string]));
    setBusy(true);
    try {
      await api.post('/api/rbac/alliance/assignment', {
        ...(names.length > 0 ? { allianceNames: names } : {}),
        ...(wantsAllBypass ? { allBypass: true } : {}),
        [mode]: targets,
        ...(mode === 'assign' && Object.keys(assignNames).length > 0 ? { assignNames } : {}),
      });
      const labels = [...names, ...(wantsAllBypass ? [ALL_BYPASS_LABEL] : [])];
      notify({
        type: 'success',
        header: mode === 'assign' ? 'Alliance access granted' : 'Alliance access revoked',
        content: `${mode === 'assign' ? 'Granted' : 'Revoked'} ${labels.map((a) => `"${a}"`).join(', ')} ${mode === 'assign' ? 'to' : 'from'} ${peopleLabel(targets.length)}.`,
      });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  // Single-row inline revoke on the "Existing alliance grants" table — same
  // bypass-the-picker reasoning as revokeRoleRow.
  const revokeAllianceRow = async (userId: string, allianceKey: string, isAllBypass: boolean) => {
    setBusy(true);
    try {
      await api.post('/api/rbac/alliance/assignment', {
        ...(isAllBypass ? { allBypass: true } : { allianceNames: [allianceKey] }),
        remove: [userId],
      });
      notify({
        type: 'success',
        header: 'Alliance access revoked',
        content: `Revoked ${isAllBypass ? ALL_BYPASS_LABEL : allianceKey} from ${userId}.`,
      });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Update failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  // Multi-row bulk revoke — only admin_direct-sourced grants are revocable
  // at all (existing rule, same as the per-row link), so non-revocable
  // selections are silently skipped and called out in the result toast
  // rather than failing the whole batch. Groups the rest by
  // allianceId/bypass (the endpoint is scoped that way) and fires one
  // request per group.
  const bulkRevokeAlliances = async () => {
    if (selectedGrantRows.length === 0) return;
    const revocable = selectedGrantRows.filter((g) => g.sources.includes('admin_direct'));
    if (revocable.length === 0) {
      notify({ type: 'error', header: 'Nothing to revoke', content: 'None of the selected grants are admin-granted — only admin-granted access can be revoked here.' });
      return;
    }
    setBusy(true);
    try {
      const byAlliance = new Map<string, string[]>();
      for (const g of revocable) {
        const key = g.isAllBypass ? ALL_BYPASS_VALUE : g.allianceId;
        byAlliance.set(key, [...(byAlliance.get(key) ?? []), g.userId]);
      }
      await Promise.all(
        [...byAlliance.entries()].map(([key, userIds]) =>
          api.post('/api/rbac/alliance/assignment', {
            ...(key === ALL_BYPASS_VALUE ? { allBypass: true } : { allianceNames: [key] }),
            remove: userIds,
          }),
        ),
      );
      const skipped = selectedGrantRows.length - revocable.length;
      notify({
        type: 'success',
        header: 'Alliance access revoked',
        content: `Revoked ${revocable.length} grant(s)${skipped ? `; skipped ${skipped} not admin-granted` : ''}.`,
      });
      setSelectedGrantRows([]);
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Bulk revoke failed', content: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageSkeleton label="Loading assignments…" />;

  if (loadError) {
    return (
      <Alert type="error" header="Failed to load assignments" action={<Button onClick={retry} iconName="refresh">Retry</Button>}>
        {loadError}
      </Alert>
    );
  }

  const selectedEmails = new Set(selectedPeople.map((p) => p.email.toLowerCase()));
  const visibleAssignments = selectedEmails.size === 0
    ? assignments
    : assignments.filter((a) => a.userEmail && selectedEmails.has(a.userEmail.toLowerCase()));
  const visibleGrants = selectedEmails.size === 0
    ? allianceGrants
    : allianceGrants.filter((g) => g.userEmail && selectedEmails.has(g.userEmail.toLowerCase()));

  const ASSIGNMENT_PAGE_SIZE = 10;
  const assignmentPagesCount = Math.max(1, Math.ceil(visibleAssignments.length / ASSIGNMENT_PAGE_SIZE));
  const assignmentPageIndexSafe = Math.min(assignmentPageIndex, assignmentPagesCount);
  const pagedAssignments = visibleAssignments.slice(
    (assignmentPageIndexSafe - 1) * ASSIGNMENT_PAGE_SIZE,
    assignmentPageIndexSafe * ASSIGNMENT_PAGE_SIZE,
  );

  const GRANT_PAGE_SIZE = 10;
  const grantPagesCount = Math.max(1, Math.ceil(visibleGrants.length / GRANT_PAGE_SIZE));
  const grantPageIndexSafe = Math.min(grantPageIndex, grantPagesCount);
  const pagedGrants = visibleGrants.slice((grantPageIndexSafe - 1) * GRANT_PAGE_SIZE, grantPageIndexSafe * GRANT_PAGE_SIZE);

  return (
    <SpaceBetween size="l">
      <FormField
        label="People"
        description="Add one or more people, then assign or revoke below — one request covers everyone added. Toggle on Microsoft directory search to find and grant access to someone new."
        errorText={peopleError}
      >
        <SpaceBetween size="xs">
          <SpaceBetween direction="horizontal" size="xs">
            <Box>
              <PersonAutosuggest
                value={draftPersonValue}
                onChange={(v, name) => {
                  setDraftPersonValue(v);
                  setDraftPersonName(name ?? null);
                }}
                people={roster}
                api={api}
              />
            </Box>
            <Button onClick={addPerson} disabled={!draftPersonValue.trim()}>Add person</Button>
          </SpaceBetween>
          {selectedPeople.length > 0 && (
            <TokenGroup
              items={selectedPeople.map((p) => ({ label: p.name || p.email, dismissLabel: `Remove ${p.name || p.email}` }))}
              onDismiss={({ detail }) => removePersonAt(detail.itemIndex)}
            />
          )}
        </SpaceBetween>
      </FormField>

      <SpaceBetween size="m">
        <Header variant="h3">Roles</Header>
        <FormField label="Role" errorText={roleError}>
          <Select
            selectedOption={role}
            placeholder="Choose a role"
            options={roles.map((r) => ({ value: r.id, label: r.role_name }))}
            onChange={(e) => {
              setRole(e.detail.selectedOption as { value: string; label: string });
              setRoleError(null);
            }}
          />
        </FormField>
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" loading={busy} onClick={() => actRole('assign')}>Assign role</Button>
          <Button loading={busy} onClick={() => actRole('remove')}>Revoke role</Button>
        </SpaceBetween>
        {visibleAssignments.length === 0 ? (
          <EmptyState
            title={selectedEmails.size > 0 ? 'No role assignments for the added people' : 'No role assignments'}
            description={
              selectedEmails.size > 0
                ? 'None of the people currently added hold a role yet — assign one above.'
                : 'Assign a role above to see it listed here.'
            }
          />
        ) : (
          <PlatformTable
            title={selectedEmails.size > 0 ? 'Role assignments for the added people' : 'Existing role assignments'}
            items={pagedAssignments}
            totalCount={visibleAssignments.length}
            variant="container"
            selectionType="multi"
            trackBy={(a) => `${a.userId}#${a.roleId}`}
            selectedItems={selectedAssignmentRows}
            onSelectionChange={(e) => setSelectedAssignmentRows(e.detail.selectedItems)}
            actions={
              <Button onClick={bulkRevokeRoles} disabled={selectedAssignmentRows.length === 0} loading={busy}>
                Revoke selected ({selectedAssignmentRows.length})
              </Button>
            }
            pagination={
              <Pagination
                currentPageIndex={assignmentPageIndexSafe}
                pagesCount={assignmentPagesCount}
                onChange={(e) => setAssignmentPageIndex(e.detail.currentPageIndex)}
              />
            }
            columnDefinitions={[
              { header: 'User', cell: (a) => a.userName || a.userEmail || a.userId },
              { header: 'Role', cell: (a) => roleLabel(a.roleId) },
              { header: 'Assigned at', cell: (a) => a.assignedAt ?? '—' },
              { header: 'Revoke', cell: (a) => <Button variant="inline-link" onClick={() => revokeRoleRow(a.userId, a.roleId)}>Revoke</Button> },
            ]}
          />
        )}
      </SpaceBetween>

      <SpaceBetween size="m">
        <Header variant="h3" description="Manual grants for people whose alliance scope AirTable doesn't govern — a manually-assigned team's own access, not an AirTable-sourced LAP/AM title (those come from the sync).">
          Alliance access
        </Header>
        <FormField
          label="Alliance(s)"
          description={`Pick "${ALL_BYPASS_LABEL}" for the universal bypass, or one or more named alliances from the active alliance list (Master view) — the same source the AirTable sync itself treats as authoritative.`}
          errorText={allianceError}
        >
          <Multiselect
            selectedOptions={allianceNames}
            placeholder="Choose one or more alliances"
            options={[{ value: ALL_BYPASS_VALUE, label: ALL_BYPASS_LABEL }, ...allianceCatalog]}
            onChange={(e) => {
              setAllianceNames(e.detail.selectedOptions as { value: string; label: string }[]);
              setAllianceError(null);
            }}
            filteringType="auto"
          />
        </FormField>
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" loading={busy} onClick={() => actAlliance('assign')}>Grant access</Button>
          <Button loading={busy} onClick={() => actAlliance('remove')}>Revoke access</Button>
        </SpaceBetween>
        {visibleGrants.length === 0 ? (
          <EmptyState
            title={selectedEmails.size > 0 ? 'No alliance grants for the added people' : 'No alliance grants'}
            description={
              selectedEmails.size > 0
                ? 'None of the people currently added have alliance access yet — grant some above.'
                : 'Grant alliance access above to see it listed here.'
            }
          />
        ) : (
          <PlatformTable
            title={selectedEmails.size > 0 ? 'Alliance grants for the added people (any source)' : 'Existing alliance grants (any source)'}
            items={pagedGrants}
            totalCount={visibleGrants.length}
            variant="container"
            selectionType="multi"
            trackBy={(g) => `${g.userId}#${g.isAllBypass ? 'ALL' : g.allianceId}`}
            selectedItems={selectedGrantRows}
            onSelectionChange={(e) => setSelectedGrantRows(e.detail.selectedItems)}
            actions={
              <Button onClick={bulkRevokeAlliances} disabled={selectedGrantRows.length === 0} loading={busy}>
                Revoke selected ({selectedGrantRows.length})
              </Button>
            }
            pagination={
              <Pagination
                currentPageIndex={grantPageIndexSafe}
                pagesCount={grantPagesCount}
                onChange={(e) => setGrantPageIndex(e.detail.currentPageIndex)}
              />
            }
            columnDefinitions={[
              { header: 'User', cell: (g) => g.userName || g.userEmail || g.userId },
              { header: 'Alliance', cell: (g) => (g.isAllBypass ? ALL_BYPASS_LABEL : g.allianceId) },
              { header: 'Source(s)', cell: (g) => g.sources.join(', ') },
              {
                header: 'Revoke',
                cell: (g) =>
                  g.sources.includes('admin_direct') ? (
                    <Button variant="inline-link" onClick={() => revokeAllianceRow(g.userId, g.allianceId, g.isAllBypass ?? false)}>Revoke</Button>
                  ) : (
                    <Box color="text-body-secondary">Not admin-granted</Box>
                  ),
              },
            ]}
          />
        )}
      </SpaceBetween>
    </SpaceBetween>
  );
}

// ── Audit Log ─────────────────────────────────────────────────────────────────
type AuditSource =
  | 'airtable_sync'
  | 'admin'
  | 'access_request'
  | 'first_login'
  | 'bootstrap'
  | 'config'
  | 'ai_tool';
type AuditAction =
  | 'alliance_granted' | 'alliance_revoked' | 'alliance_pending_queued' | 'alliance_pending_removed'
  | 'role_assigned' | 'role_revoked' | 'role_pending_queued' | 'role_pending_removed'
  | 'cohort_granted' | 'all_granted' | 'all_revoked' | 'sync_aborted' | 'sync_error' | 'sync_failed'
  | 'sync_identity_conflict' | 'sync_role_tag_drift' | 'sync_threshold_override'
  | 'registry_upserted' | 'registry_enabled' | 'registry_disabled' | 'registry_deleted'
  | 'ai_config_updated' | 'airtable_config_updated' | 'email_config_updated' | 'email_test_sent'
  | 'onboarding_approved' | 'onboarding_rejected' | 'ai_tool_called';
interface AuditEvent {
  id: string;
  timestamp: string;
  source: AuditSource;
  trigger?: 'manual' | 'scheduled';
  family?: string;
  actorLabel?: string;
  action: AuditAction;
  targetLabel?: string;
  allianceName?: string;
  roleId?: string;
  cohortId?: string;
  reason?: string;
  /** Admin-setting changes and AI tool calls. */
  appId?: string;
  /** Which settings changed (names only, never values). */
  detail?: string;
  toolName?: string;
  ok?: boolean;
  latencyMs?: number;
}

const SOURCE_LABEL: Record<AuditSource, string> = {
  airtable_sync: 'AirTable sync',
  admin: 'Admin',
  access_request: 'Access request',
  first_login: 'First login',
  bootstrap: 'Bootstrap (auto-seed)',
  config: 'Admin settings',
  ai_tool: 'AI tool call',
};

const ACTION_LABEL: Record<AuditAction, string> = {
  alliance_granted: 'Alliance access granted',
  alliance_revoked: 'Alliance access revoked',
  alliance_pending_queued: 'Alliance access queued (pending first login)',
  alliance_pending_removed: 'Pending alliance access removed (fell off roster)',
  role_assigned: 'Role assigned',
  role_revoked: 'Role revoked',
  role_pending_queued: 'Role queued (pending first login)',
  role_pending_removed: 'Pending role assignment removed (fell off roster)',
  cohort_granted: 'Cohort access granted',
  all_granted: 'All-alliance access granted',
  all_revoked: 'All-alliance access revoked',
  sync_aborted: 'Sync run aborted',
  sync_error: 'Sync write failed',
  sync_failed: 'Sync run failed (see reason)',
  sync_identity_conflict: 'Duplicate Cognito account for a roster email — access frozen',
  sync_role_tag_drift: 'Role tag drift — access frozen for affected rows (check AirTable)',
  sync_threshold_override: 'Delta-threshold override used for this run (see reason)',
  registry_upserted: 'App registered or updated',
  registry_enabled: 'App enabled',
  registry_disabled: 'App disabled',
  registry_deleted: 'App removed from the registry',
  ai_config_updated: 'Assistant settings changed',
  airtable_config_updated: 'Assistant data-source settings changed',
  email_config_updated: 'Email settings changed',
  email_test_sent: 'Test email sent',
  onboarding_approved: 'App onboarding request approved',
  onboarding_rejected: 'App onboarding request rejected',
  ai_tool_called: 'Assistant ran a tool',
};

const SOURCE_OPTIONS: { value: AuditSource | 'all'; label: string }[] = [
  { value: 'all', label: 'All sources' },
  { value: 'airtable_sync', label: SOURCE_LABEL.airtable_sync },
  { value: 'admin', label: SOURCE_LABEL.admin },
  { value: 'access_request', label: SOURCE_LABEL.access_request },
  { value: 'first_login', label: SOURCE_LABEL.first_login },
  { value: 'bootstrap', label: SOURCE_LABEL.bootstrap },
  // AI tool calls are high volume and live in their own view (ToolCallAuditPanel).
  { value: 'config', label: SOURCE_LABEL.config },
];

const eventDetail = (e: AuditEvent) =>
  e.allianceName ?? e.roleId ?? e.cohortId ?? e.appId ?? (e.action === 'all_granted' || e.action === 'all_revoked' ? 'All alliances' : '—');
/** The Notes column: which settings changed, then any reason. */
const eventNotes = (e: AuditEvent) => [e.detail, e.reason].filter(Boolean).join(' · ') || '—';
const eventTrigger = (e: AuditEvent) => (e.trigger ? (e.trigger === 'scheduled' ? 'Nightly schedule' : 'Manual') : '—');

/**
 * The unified RBAC audit trail (ADR 0013) — every real grant/revoke/
 * assignment, however it happened: the AirTable sync (manual or nightly),
 * a direct admin assignment, an access-request approval, or a pending-bind
 * converting to a real grant on someone's first login. One flat,
 * reverse-chronological list rather than a run-centric view — most sources
 * here don't have a "run" to group by. Read-only; admin-only, same gate as
 * this whole page.
 */
function AuditLogTab({ api }: { api: Api }) {
  const { notify } = useNotify();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [source, setSource] = useState<AuditSource | 'all'>('all');
  const [pageIndex, setPageIndex] = useState(1);

  const load = async () => {
    try {
      const res = await api.get<{ events: AuditEvent[]; nextCursor?: string }>('/api/rbac/audit-log');
      setEvents(res.events);
      setNextCursor(res.nextCursor);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [api]);
  const retry = () => {
    setLoading(true);
    load();
  };

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get<{ events: AuditEvent[]; nextCursor?: string }>(
        `/api/rbac/audit-log?cursor=${encodeURIComponent(nextCursor)}`,
      );
      setEvents((prev) => [...prev, ...res.events]);
      setNextCursor(res.nextCursor);
    } catch (e) {
      notify({
        type: 'error',
        header: 'Failed to load older audit events',
        content: (e as Error).message,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // The visible result set shifts whenever the filter or source changes —
  // stay on page 1 rather than showing a now-meaningless page N.
  useEffect(() => {
    setPageIndex(1);
  }, [filterText, source]);

  if (loading) return <PageSkeleton label="Loading audit log…" />;

  const needle = filterText.trim().toLowerCase();
  const filtered = events
    .filter((e) => source === 'all' || e.source === source)
    .filter(
      (e) =>
        !needle ||
        (e.targetLabel?.toLowerCase().includes(needle) ?? false) ||
        (e.actorLabel?.toLowerCase().includes(needle) ?? false) ||
        eventDetail(e).toLowerCase().includes(needle) ||
        ACTION_LABEL[e.action].toLowerCase().includes(needle),
    );

  const PAGE_SIZE = 25;
  const pagesCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageIndexSafe = Math.min(pageIndex, pagesCount);
  const pagedEvents = filtered.slice((pageIndexSafe - 1) * PAGE_SIZE, pageIndexSafe * PAGE_SIZE);

  return (
    <SpaceBetween size="m">
      {loadError ? (
        <Alert type="error" header="Failed to load audit log" action={<Button onClick={retry} iconName="refresh">Retry</Button>}>
          {loadError}
        </Alert>
      ) : events.length === 0 ? (
        <EmptyState
          title="No audit events yet"
          description="Every real RBAC change — from the AirTable sync, a direct admin assignment, an access-request approval, or someone's first login applying a queued grant — will show up here."
        />
      ) : (
        <PlatformTable
          title="Audit log"
          items={pagedEvents}
          totalCount={filtered.length}
          variant="container"
          filter={
            <TextFilter
              filteringText={filterText}
              onChange={(e) => setFilterText(e.detail.filteringText)}
              filteringPlaceholder="Search by person, actor, or alliance/role"
              countText={`${filtered.length} of ${events.length}`}
            />
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <FormField label="Source">
                <Select
                  selectedOption={SOURCE_OPTIONS.find((o) => o.value === source) ?? null}
                  options={SOURCE_OPTIONS}
                  onChange={(e) => setSource(e.detail.selectedOption.value as AuditSource | 'all')}
                />
              </FormField>
              {nextCursor ? (
                <Box padding={{ top: 'l' }}>
                  <Button onClick={loadMore} loading={loadingMore} iconName="download">
                    Load older events
                  </Button>
                </Box>
              ) : null}
            </SpaceBetween>
          }
          footer={
            nextCursor ? (
              <Box textAlign="center" padding={{ top: 's', bottom: 's' }}>
                <Button onClick={loadMore} loading={loadingMore} iconName="download">
                  Load older events
                </Button>
              </Box>
            ) : null
          }
          pagination={
            <Pagination currentPageIndex={pageIndexSafe} pagesCount={pagesCount} onChange={(e) => setPageIndex(e.detail.currentPageIndex)} />
          }
          columnDefinitions={[
            { header: 'When', cell: (e) => new Date(e.timestamp).toLocaleString() },
            { header: 'Source', cell: (e) => (e.trigger ? `${SOURCE_LABEL[e.source]} (${eventTrigger(e)})` : SOURCE_LABEL[e.source]) },
            { header: 'Actor', cell: (e) => e.actorLabel || '—' },
            {
              header: 'Change',
              cell: (e) => (
                <StatusIndicator
                  type={
                    e.action === 'sync_aborted' || e.action === 'sync_error' || e.action === 'sync_failed'
                      ? 'error'
                      : e.action === 'sync_role_tag_drift' || e.action === 'sync_identity_conflict' || e.action === 'sync_threshold_override'
                        || e.action === 'registry_deleted' || e.action === 'onboarding_rejected' || e.action === 'registry_disabled'
                        ? 'warning'
                        : 'success'
                  }
                >
                  {ACTION_LABEL[e.action]}
                </StatusIndicator>
              ),
            },
            { header: 'Person', cell: (e) => e.targetLabel || '—' },
            { header: 'Alliance / Role / App', cell: (e) => eventDetail(e) },
            { header: 'Notes', cell: (e) => eventNotes(e) },
          ]}
          empty={<EmptyState title="No matches" description="No audit events match that search." />}
        />
      )}
    </SpaceBetween>
  );
}

// ── AI tool calls (ADR 0033) ──────────────────────────────────────────────────

/** The last three months, newest first, as `YYYY-MM` (UTC, matching how the API partitions the records). */
function recentMonths(now: Date = new Date()): { value: string; label: string }[] {
  return [0, 1, 2].map((back) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
    return {
      value: d.toISOString().slice(0, 7),
      label: d.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    };
  });
}

const AUDIT_VIEW_OPTIONS = [
  { value: 'changes', label: 'Access and settings changes' },
  { value: 'tools', label: 'AI tool calls' },
];

/**
 * The Audit Log tab. Two views, because the records are different in kind and
 * volume: access and settings changes (the original log, kept indefinitely) and
 * the assistant's tool calls (one row per execution, kept about 400 days, in
 * monthly partitions of their own).
 */
function AuditTab({ api }: { api: Api }) {
  const [view, setView] = useState<'changes' | 'tools'>('changes');
  return (
    <SpaceBetween size="m">
      <FormField label="View">
        <Select
          selectedOption={AUDIT_VIEW_OPTIONS.find((o) => o.value === view) ?? null}
          options={AUDIT_VIEW_OPTIONS}
          onChange={(e) => setView(e.detail.selectedOption.value as 'changes' | 'tools')}
        />
      </FormField>
      {view === 'changes' ? <AuditLogTab api={api} /> : <ToolCallAuditPanel api={api} />}
    </SpaceBetween>
  );
}

/**
 * Which data tools the assistant ran, for whom, against which app, and how it
 * went. Names, outcomes and timings only: a tool's inputs and results are never
 * recorded. Read-only, admin-only, one month at a time.
 */
function ToolCallAuditPanel({ api }: { api: Api }) {
  const { notify } = useNotify();
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[0]!.value);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [pageIndex, setPageIndex] = useState(1);

  const load = async (forMonth: string) => {
    setLoading(true);
    try {
      const res = await api.get<{ events: AuditEvent[]; nextCursor?: string }>(
        `/api/rbac/audit-log?stream=tool&month=${encodeURIComponent(forMonth)}`,
      );
      setEvents(res.events);
      setNextCursor(res.nextCursor);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(month);
  }, [api, month]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get<{ events: AuditEvent[]; nextCursor?: string }>(
        `/api/rbac/audit-log?stream=tool&month=${encodeURIComponent(month)}&cursor=${encodeURIComponent(nextCursor)}`,
      );
      setEvents((prev) => [...prev, ...res.events]);
      setNextCursor(res.nextCursor);
    } catch (e) {
      notify({ type: 'error', header: 'Failed to load older tool calls', content: (e as Error).message });
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPageIndex(1);
  }, [filterText, month]);

  const needle = filterText.trim().toLowerCase();
  const filtered = events.filter(
    (e) =>
      !needle ||
      (e.actorLabel?.toLowerCase().includes(needle) ?? false) ||
      (e.appId?.toLowerCase().includes(needle) ?? false) ||
      (e.toolName?.toLowerCase().includes(needle) ?? false),
  );
  const PAGE_SIZE = 25;
  const pagesCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageIndexSafe = Math.min(pageIndex, pagesCount);
  const paged = filtered.slice((pageIndexSafe - 1) * PAGE_SIZE, pageIndexSafe * PAGE_SIZE);

  const monthSelector = (
    <FormField label="Month">
      <Select
        selectedOption={months.find((m) => m.value === month) ?? null}
        options={months}
        onChange={(e) => setMonth(e.detail.selectedOption.value as string)}
      />
    </FormField>
  );

  if (loading) return <PageSkeleton label="Loading tool calls…" />;

  return (
    <SpaceBetween size="m">
      {loadError ? (
        <Alert
          type="error"
          header="Failed to load tool calls"
          action={<Button onClick={() => load(month)} iconName="refresh">Retry</Button>}
        >
          {loadError}
        </Alert>
      ) : events.length === 0 ? (
        <SpaceBetween size="m">
          {monthSelector}
          <EmptyState
            title="No tool calls recorded for this month"
            description="Each time the assistant runs one of an app's data tools, it is recorded here: who asked, which app and tool, and whether it worked. What was asked and what came back is never stored."
          />
        </SpaceBetween>
      ) : (
        <PlatformTable
          title="AI tool calls"
          items={paged}
          totalCount={filtered.length}
          variant="container"
          filter={
            <TextFilter
              filteringText={filterText}
              onChange={(e) => setFilterText(e.detail.filteringText)}
              filteringPlaceholder="Search by person, app, or tool"
              countText={`${filtered.length} of ${events.length}`}
            />
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {monthSelector}
              {nextCursor ? (
                <Box padding={{ top: 'l' }}>
                  <Button onClick={loadMore} loading={loadingMore} iconName="download">
                    Load older calls
                  </Button>
                </Box>
              ) : null}
            </SpaceBetween>
          }
          pagination={
            <Pagination currentPageIndex={pageIndexSafe} pagesCount={pagesCount} onChange={(e) => setPageIndex(e.detail.currentPageIndex)} />
          }
          columnDefinitions={[
            { header: 'When', cell: (e) => new Date(e.timestamp).toLocaleString() },
            { header: 'Person', cell: (e) => e.actorLabel || '—' },
            { header: 'App', cell: (e) => e.appId ?? '—' },
            { header: 'Tool', cell: (e) => e.toolName ?? '—' },
            {
              header: 'Result',
              cell: (e) => (
                <StatusIndicator type={e.ok ? 'success' : 'error'}>
                  {e.ok ? 'Succeeded' : `Failed${e.reason ? ` (${e.reason})` : ''}`}
                </StatusIndicator>
              ),
            },
            { header: 'Time', cell: (e) => (e.latencyMs === undefined ? '—' : `${e.latencyMs} ms`) },
          ]}
          empty={<EmptyState title="No matches" description="No tool calls match that search." />}
        />
      )}
    </SpaceBetween>
  );
}
